import {
  baseStyles,
  journeyStyles,
  institutionStyles,
  skillsStyles,
  courseStyles,
  cohortStyles,
  questionSetStyles,
  practiceStyles,
} from 'analytics_dashboard/components/custom-styles';
import { useEffect, useState } from 'react';
import t from 'react-translate';
import { SurveyCharts } from 'redux/schemas/api/analytics';
import {
  AnalyticsDashBoardType,
  QrveyConfig,
  QrveyPanel,
  questionSetkeyMapping,
  QrveyState,
  QrveyInstance,
  ChartType,
} from 'redux/schemas/app/analytics';
import { each } from 'underscore';

const setGlobal = (key: string, value: any) => {
  (window as any)[key] = value;
};

const clearGlobal = (key: string) => {
  if ((window as any)[key]) {
    delete (window as any)[key];
  }
};

// Provided by qrvey, this is how we reset the variables so we can switch dashboards and
// provide different CSS every time.
export const resetQrveyValues = () => {
  if (typeof window.customEUStyle !== 'undefined' && window.customEUStyle !== '') {
    window.customEUStyle = undefined;
  }
};

const getOverwrittenStyles = (type: AnalyticsDashBoardType, pageIdKey: string) => `
  ${baseStyles}
  ${type === AnalyticsDashBoardType.LEARNING_JOURNEY ? journeyStyles : ''}
  ${type === AnalyticsDashBoardType.INSTITUTION ? institutionStyles : ''}
  ${(type === AnalyticsDashBoardType.COURSE && pageIdKey === 'skills') ? skillsStyles : ''}
  ${(type === AnalyticsDashBoardType.COURSE && pageIdKey !== 'skills') ? courseStyles : ''}
  ${type === AnalyticsDashBoardType.COHORTS ? cohortStyles : ''}
  ${type === AnalyticsDashBoardType.QUESTION_SET ? questionSetStyles : ''}
  ${type === AnalyticsDashBoardType.COHORT_QUESTION_SET ? questionSetStyles : ''}
  ${type === AnalyticsDashBoardType.PRACTICE_ANALYTICS ? practiceStyles : ''}
`;

const initializeQrvey = (
  qrveydata: QrveyState | QrveyInstance,
  type: AnalyticsDashBoardType,
  pageIdKey: string,
): { config: QrveyConfig; panels?: QrveyPanel[]; title?: string[] } | null => {
  const data: QrveyInstance = type === AnalyticsDashBoardType.COHORT_QUESTION_SET || type === AnalyticsDashBoardType.QUESTION_SET ? qrveydata : qrveydata[type];
  if (!data) return null;
  const {
    token,
    appId,
    userId,
    host,
    pageId,
    pageIds,
    panels: qrveyPanels,
    userFilters,
    topPageId,
    panelsPageId,
    title: questionSetTitle,
    type: questionSetType,
  } = data;

  const config: QrveyConfig = {
    qv_token: token,
    domain: host,
    app_id: appId,
    user_id: userId,
    customCSSRules: getOverwrittenStyles(type, pageIdKey),
    custom_styles: true,
    downloadingBoxText: t.QRVEY.DOWNLOAD_BOX(),
    automaticDownload: false,
    featurePermission: { panels: { global: { hide_CSV: true } } },
  };

  let panels: QrveyPanel[] | undefined;
  let title: string[] | [];

  switch (type) {
    case AnalyticsDashBoardType.COHORTS:
      config.page_id = pageId;
      break;

    case AnalyticsDashBoardType.PRACTICE_ANALYTICS:
      config.page_id = pageId;
      break;

    case AnalyticsDashBoardType.COURSE:
      config.page_id = pageIds[pageIdKey];
      break;

    case AnalyticsDashBoardType.LICENSE:
      config.page_id = pageId;
      config.userFilters = userFilters;
      break;

    case AnalyticsDashBoardType.MENTORSHIP_PROGRAM:
      config.page_id = pageId;
      break;

    case AnalyticsDashBoardType.QUESTION_SET:
    case AnalyticsDashBoardType.COHORT_QUESTION_SET: {
      if (type === AnalyticsDashBoardType.QUESTION_SET) {
        config.dashboardId = topPageId;
      }

      if (qrveyPanels) {
        const questionSetPanels: QrveyPanel[] = [];
        each(qrveyPanels, (panel, index) => {
          let isMetric = false;
          const anPanelConfig: any = {
            domain: host,
            qv_token: token,
            qrveyId: panel.dataSetId,
            user_id: userId,
            app_id: appId,
            userFilters: panel.userFilters,
            panel: {
              header: {
                visible: true,
                filter: false,
                menu: {
                  enable: true,
                  options: [
                    {
                      key: 'download',
                      enable: true,
                      options: [
                        { key: 'jpg', enable: true },
                        { key: 'pdf', enable: true },
                        { key: 'csv', enable: false },
                        { key: 'csv-summary', enable: true },
                        { key: 'excel', enable: true },
                      ],
                    },
                  ],
                },
              },
            },
          };
          if (panel.chartId) {
            anPanelConfig.type = 'CHART';
            anPanelConfig.chart_id = panel.chartId;
          }
          if (panel.metricId) {
            isMetric = true;
            anPanelConfig.type = 'METRIC';
            anPanelConfig.metric_id = panel.metricId;
          }
          setGlobal(`anPanelConfig${index}`, anPanelConfig);

          const questionSetPanel: QrveyPanel = { ...panel };
          if (panel.chartType === ChartType.COMPARISON || panel.chartType === ChartType.ALL || panel.chartType === ChartType.ALL_LEARNERS) {
            if (panel.chartType !== ChartType.ALL_LEARNERS) {
              questionSetPanel.title = panel.chartType === ChartType.COMPARISON
                ? t.QRVEY.COMPARE_RESULTS()
                : t.QRVEY.ALL_QUESTIONS();
            }
            questionSetPanel.className = 'full-panel';
          }
          if (isMetric) {
            questionSetPanel.className = 'number-panel';
          }
          if (panel.chartType === ChartType.LIKERT_SCALE_AVG || panel.chartType === ChartType.LIKERT_SCALE_QUESTION) {
            if (panel.chartType === ChartType.LIKERT_SCALE_AVG) {
              questionSetPanel.customTitle = panel.title;
            }
            questionSetPanel.title = null;
          }

          questionSetPanels[index] = questionSetPanel;
        });
        panels = questionSetPanels;
      }

      if (questionSetTitle) {
        title = [t.COURSE_ANALYTICS[questionSetkeyMapping[questionSetType]](), questionSetTitle];
      }
      break;
    }

    default:
      break;
  }

  return { config, panels, title };
};

export const useQrveyInitializer = (
  data: QrveyState,
  isReady: boolean,
  isLoading: boolean,
  keyName: string,
  pageIdKey: string,
  type: AnalyticsDashBoardType,
) => {
  const [pageTitle, setPageTitle] = useState<string[] | undefined>([]);
  const [panels, setPanels] = useState<QrveyPanel[]>();
  const [isError, setIsError] = useState<boolean>(false);

  const configKeyName = keyName || 'config';

  useEffect(() => {
    if (data && isReady && !isLoading) {
      const qrveyConfig = initializeQrvey(data, type, pageIdKey);
      if (!qrveyConfig) {
        setIsError(true);
        return;
      }
      const { config, panels: qrveyPanels, title } = qrveyConfig;

      // Assign config to global window object
      clearGlobal(configKeyName);
      setGlobal(configKeyName, config);

      setPanels(qrveyPanels);
      setPageTitle(title);
    }
  }, [data, isReady, isLoading, type, pageIdKey, configKeyName]);

  useEffect(() => () => {
    // Cleanup when component unmounts
    clearGlobal(configKeyName);
    resetQrveyValues();
  }, [configKeyName, type]);

  return { panels, pageTitle, isError };
};

const createPanelConfig = (
  panel: QrveyPanel,
  token: string,
  host: string,
  userId: string,
  appId: string,
) => ({
  domain: host,
  qv_token: token,
  qrveyId: panel.dataSetId,
  user_id: userId,
  app_id: appId,
  userFilters: panel.userFilters,
  chart_id: panel.chartId,
  title: panel.title,
  type: 'CHART',
  panel: {
    header: {
      visible: true,
      filter: false,
      menu: {
        enable: true,
        options: [
          {
            key: 'download',
            enable: true,
            options: [
              { key: 'jpg', enable: true },
              { key: 'pdf', enable: true },
              { key: 'csv', enable: false },
              { key: 'csv-summary', enable: true },
              { key: 'excel', enable: true },
            ],
          },
        ],
      },
    },
  },
});
/**
 * Initializes and manages pre/post survey panels for comparison views.
 * Pairs pre-course and post-course survey panels by index position for side-by-side comparison.
 * Assumes the pre and post survey data are ordered consistently so that matching panels share the same index.
 *
 * @param surveyCharts - Survey data containing both pre and post course panels
 * @param isReady - Whether Qrvey scripts are loaded and ready
 * @param isLoading - Whether data is currently being fetched
 * @returns Object containing panels array and error state
 */
export const useSurveyPanelsInitializer = (
  surveyCharts: SurveyCharts,
  isReady: boolean,
  isLoading: boolean,
) => {
  const [panels, setPanels] = useState<QrveyPanel[]>();
  const [isError, setIsError] = useState<boolean>(false);

  useEffect(() => {
    if (!surveyCharts || !isReady || isLoading) return;

    const getPanelConfig = (data: SurveyCharts) => {
      try {
        if (!data?.postCourse || !data?.preCourse) {
          return null;
        }

        const {
          postCourse: { host, token: postToken, userId, appId, panels: postPanels },
          preCourse: { panels: prePanels, token: preToken },
        } = data;

        if (!postPanels?.length || !prePanels?.length) {
          return null;
        }

        const maxLength = Math.max(prePanels.length, postPanels.length);
        let globalIndex = 0;

        const allPanels: QrveyPanel[] = [];

        each(Array(maxLength), (_, i) => {
          const prePanel = prePanels[i];
          const postPanel = postPanels[i];

          // Skip only if both panels are missing
          if (!prePanel && !postPanel) {
            return;
          }

          // Handle unpaired panels (when array lengths differ)
          if (!prePanel && postPanel) {
            setGlobal(
              `anPanelConfig${globalIndex}`,
              createPanelConfig(postPanel, postToken, host, userId, appId),
            );
            allPanels.push({
              ...postPanel,
              className: 'full-panel',
              titleInfo: t.QRVEY.POST_SURVEY(),
            });
            globalIndex += 1;
            return;
          }

          if (prePanel && !postPanel) {
            setGlobal(
              `anPanelConfig${globalIndex}`,
              createPanelConfig(prePanel, preToken, host, userId, appId),
            );
            allPanels.push({
              ...prePanel,
              className: 'full-panel',
              titleInfo: t.QRVEY.PRE_SURVEY(),
            });
            globalIndex += 1;
            return;
          }

          // Create and set pre-course panel config
          setGlobal(
            `anPanelConfig${globalIndex}`,
            createPanelConfig(prePanel, preToken, host, userId, appId),
          );
          globalIndex += 1;

          // Create and set post-course panel config
          setGlobal(
            `anPanelConfig${globalIndex}`,
            createPanelConfig(postPanel, postToken, host, userId, appId),
          );
          globalIndex += 1;

          // Determine title display logic for paired panels
          const titlesMatch = prePanel.title === postPanel.title;
          // paired panel will be having same type
          // so no need to check postPanel type
          /**
           * share title conditions:
           * 1. If titles are identical, only pre-panel shows title.
           * 2. If post-panel title is missing, pre-panel shows title.
           * 3. If pre-panel is "All Questions" chart, use generic title for both.
           */
          const isAllQuestionsChart = prePanel.chartType === ChartType.ALL;
          const postHasNoTitle = !postPanel.title;
          const shouldShareTitle = titlesMatch || postHasNoTitle;

          let customTitle = null;
          if (isAllQuestionsChart) {
            customTitle = t.QRVEY.SURVEY_ALL();
          } else if (shouldShareTitle) {
            customTitle = prePanel.title;
          }

          // Create paired panels with consistent title handling
          const prePanelCopy: QrveyPanel = {
            ...prePanel,
            className: 'half-panel',
            customTitle,
            title: customTitle ? null : prePanel.title,
            titleInfo: t.QRVEY.PRE_SURVEY(),
          };

          const postPanelCopy: QrveyPanel = {
            ...postPanel,
            className: 'half-panel',
            customTitle: null, // customTitle only for pre-panel, serves as shared title
            title: titlesMatch ? null : postPanel.title,
            titleInfo: t.QRVEY.POST_SURVEY(),
          };

          allPanels.push(prePanelCopy, postPanelCopy);
        });

        return allPanels;
      } catch (error) {
        return null;
      }
    };

    const surveyPanels = getPanelConfig(surveyCharts);

    if (!surveyPanels) {
      setIsError(true);
      return;
    }

    setPanels(surveyPanels);
    setIsError(false);
  }, [surveyCharts, isReady, isLoading]);

  useEffect(() => () => {
    // Cleanup global state
    resetQrveyValues();
    // Clear all panel configs for both pre- and post-course panels
    const prePanels = surveyCharts?.preCourse?.panels || [];
    const postPanels = surveyCharts?.postCourse?.panels || [];
    const totalPanels = prePanels.length + postPanels.length;
    each(Array(totalPanels), (_, index) => {
      clearGlobal(`anPanelConfig${index}`);
    });
  }, [surveyCharts]);

  return { panels, isError };
};
